import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { color } from '@shared/constants/Styles';

/**
 * Typography.tsx
 * This file contains the bare minimum typography settings.
 * All core typography settings should be defined here.
 * This is raw typography, no padding or margins should be defined here.
 * For extended typography for content see ContentTypography.tsx
 */

// MIXINS
export const MixinTruncate = css`
    display: -webkit-box;
    word-break: break-all;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

export const MixinTypographyLightSmall = css`
    font-weight: 300;
    font-size: 1rem; // 16px
    line-height: 1.3125rem; // 21px
    letter-spacing: 0.03rem;
`;

export const MixinTypographyBodyMedium = css`
    font-weight: 400;
    font-size: 1rem; // 16px
    line-height: 1.3125rem; // 21px
    letter-spacing: 0.03rem;
`;
export const MixinTypographyLightXSmall = css`
    font-weight: 300;
    font-size: 0.875rem; // 14px
    line-height: 1.125rem; // 18px
    letter-spacing: 0.03rem;
`;

export const MixinTypographyLightSubtitle = css`
    font-weight: 300;
    font-size: 0.75rem; // 12px
    line-height: 1rem; // 16px
`;

export const MixinTypographyDisplayMedium = css`
    font-weight: 900;
    font-size: 2.875rem; // 46px
    line-height: 3.25rem; // 52px
`;

export const MixinTypographyDisplaySmall = css`
    font-weight: 900;
    font-size: 2.25rem; // 36px
    line-height: 2.625rem; // 42px
`;
export const MixinTypographyDisplayXXSmall = css`
    font-weight: 800;
    font-size: 1.125rem;
    letter-spacing: 0.03rem;
    line-height: 1.313rem;
`;

// DISPLAY
export const DisplayLarge = styled.h1`
    font-weight: 900;
    font-size: 3rem; // 52px
    color: ${(props) => props.theme.color.text};
    line-height: 3.875rem; // 62px
    letter-spacing: -0.02rem;
    word-break: break-word;
`;
export const DisplayMedium = styled.h2`
    ${MixinTypographyDisplayMedium}
    color: ${(props) => props.theme.color.text};
`;
export const DisplaySmall = styled.h3`
    ${MixinTypographyDisplaySmall}
    color: ${(props) => props.theme.color.text};
`;
export const DisplayXSmall = styled.h3`
    font-weight: 900;
    font-size: 1.75rem; // 28px
    color: ${(props) => props.theme.color.text};
    line-height: 2rem; // 32px
`;

export const DisplayXXSmall = styled.h3`
    ${MixinTypographyDisplayXXSmall}
    color: ${(props) => props.theme.color.text};
`;

// TITLE
export const TitleLarge = styled.h1`
    font-weight: 700;
    font-size: 2rem; // 32px
    color: ${(props) => props.theme.color.text};
    line-height: 2.625rem; // 42px
`;
export const TitleMedium = styled.h2`
    font-weight: 700;
    font-size: 1.75rem; // 28px
    color: ${(props) => props.theme.color.text};
    line-height: 2rem; // 32px
`;
export const TitleSmall = styled.h3`
    font-weight: 700;
    font-size: 1.25rem; // 24px
    color: ${(props) => props.theme.color.text};
    line-height: 1.75rem; // 28px
`;

export const TruncateTitleSmall = styled(TitleSmall)`
    ${MixinTruncate}
`;

export const TitleXSmall = styled.p`
    font-weight: 700;
    font-size: 1rem; // 16px
    color: ${(props) => props.theme.color.text};
    line-height: 1.313rem;
`;

export const TitleXXSmall = styled.p`
    font-weight: 700;
    font-size: 0.875rem; // 14px
    color: ${(props) => props.theme.color.text};
    line-height: 1.125rem; // 18px
`;

// LABEL
export const LabelXLarge = styled.p`
    font-weight: 500;
    font-size: 1.5rem; // 24px
    color: ${(props) => props.theme.color.textBody};
    line-height: 1.75rem; // 28px
`;

export const LabelLarge = styled.p`
    font-weight: 500;
    font-size: 1.125rem; // 18px
    color: ${(props) => props.theme.color.textBody};
    line-height: 1.5rem; // 24px
`;
export const LabelMedium = styled.p`
    font-weight: 500;
    font-size: 1rem; // 16px
    color: ${(props) => props.theme.color.textBody};
    line-height: 1.313rem; // 21px
`;
export const LabelSmall = styled.p`
    font-weight: 500;
    font-size: 0.875rem; // 14px
    color: ${(props) => props.theme.color.textBody};
    line-height: 1.125rem; // 18px
`;

// BODY
export const BodyXLarge = styled.p`
    font-weight: 400;
    font-size: 2rem; // 32px
    color: ${(props) => props.theme.color.textBody};
    line-height: 2.625rem;
`;

export const BodyLarge = styled.p`
    font-weight: 400;
    font-size: 1.313rem; // 21px
    color: ${(props) => props.theme.color.textBody};
    line-height: 2rem; // 32px
`;
export const BodyMedium = styled.p`
    font-weight: 400;
    font-size: 1.125rem; // 18px
    color: ${(props) => props.theme.color.textBody};
    line-height: 1.625rem; // 26px
`;
export const BodySmall = styled.p`
    font-weight: 400;
    font-size: 1rem; // 16px
    color: ${(props) => props.theme.color.textBody};
    line-height: 1.313rem; // 21px
`;

export const BodyXSmall = styled.p`
    font-weight: 400;
    font-size: 0.875rem; // 12px
    color: ${(props) => props.theme.color.textBody};
    line-height: 1.1rem; // 18px
`;

export const LightXXLarge = styled.p`
    font-weight: 300;
    font-size: 2rem; // 32px
    color: ${(props) => props.theme.color.textBody};
    line-height: 2.5625rem; // 41px
    letter-spacing: 0.03rem;
`;
export const LightXLarge = styled.p`
    font-weight: 300;
    font-size: 1.565rem; // 25px
    color: ${(props) => props.theme.color.textBody};
    line-height: 2.5625rem; // 41px
    letter-spacing: 0.03rem;
`;

export const LightLarge = styled.p`
    font-weight: 300;
    font-size: 1.3125rem; // 21px
    color: ${(props) => props.theme.color.textBody};
    line-height: 2rem; // 32px
    letter-spacing: 0.03rem;
`;
export const LightMedium = styled.p`
    font-weight: 300;
    font-size: 1.125rem; // 18px
    color: ${(props) => props.theme.color.textBody};
    line-height: 1.625rem; // 26px
    letter-spacing: 0.03rem;
`;

export const LightSmall = styled.p`
    ${MixinTypographyLightSmall}
    color: ${(props) => props.theme.color.textBody};
`;

export const LightXSmall = styled.p`
    ${MixinTypographyLightXSmall}
    color: ${(props) => props.theme.color.textBody};
`;

export const LightSubtitle = styled.p`
    ${MixinTypographyLightSubtitle}
    color: ${(props) => props.theme.color.textBody};
`;

export const SubtitleText = styled.p`
    font-weight: 200;
    text-align: center;
    font-size: 1rem;
    line-height: 1.313rem;
`;

// CHIP
export const ChipSmall = styled(LabelSmall)`
    color: ${color.success};
    border: 1px solid ${color.success};
    background-color: #e1e6e1;
    border-radius: 0.375rem;
    padding: 0.25rem 0.5rem;
`;
export const ChipMedium = styled(LabelSmall)`
    color: ${color.success};
    border: 1px solid ${color.success};
    background-color: #e1e6e1;
    border-radius: 0.375rem;
    padding: 0.25rem 0.5rem;
`;
